import React, { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import BottomNav from "./BottomNav";
import UpgradeModal from "./UpgradeModal";
import NotificationsBanner from "./NotificationsBanner";
import { ShareProvider } from "./ShareSheet";
import { useAuth } from "../contexts/AuthContext";
import { useReminders } from "../hooks/useReminders";
import { onForegroundMessage, registerForPush } from "../lib/firebase";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

// Mobile app shell — used for every /app/* route.
// Pages render their own headers (pink gradient for Dashboard, back-arrow for sub-pages).
export const UpgradeContext = React.createContext({ open: () => {} });

export default function AppShell() {
  const [modal, setModal] = useState({ open: false, feature: "" });
  const { profile } = useAuth();
  const mounted = useRef(true);

  // Schedule in-app reminders (morning, streak, weekly, insight)
  useReminders(profile);

  // Foreground FCM handler — show toast when a push arrives while the app is open.
  // Use a mounted ref so unmount during async setup doesn't leak the listener.
  useEffect(() => {
    let unsub = () => {};
    (async () => {
      const u = await onForegroundMessage((payload) => {
        const title = payload.notification?.title || payload.data?.title || "Glow & Grow";
        const body = payload.notification?.body || payload.data?.body || "";
        toast(title, { description: body });
      });
      if (!mounted.current) {
        if (typeof u === "function") u();
        return;
      }
      if (typeof u === "function") unsub = u;
    })();
    return () => { mounted.current = false; unsub(); };
  }, []);

  // Idempotent token capture: when permission is already granted (e.g. PWA reinstall,
  // user returning from another device), make sure our fcm_tokens row exists.
  useEffect(() => {
    if (!profile?.id) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    (async () => {
      const { count } = await supabase
        .from("fcm_tokens")
        .select("token", { count: "exact", head: true })
        .eq("user_id", profile.id);
      if (!count) {
        // Re-register to capture a fresh token without showing any UI prompt.
        await registerForPush(profile.id);
      }
    })();
  }, [profile?.id]);

  const openUpgrade = (feature) => setModal({ open: true, feature });
  const closeUpgrade = () => setModal({ open: false, feature: "" });

  return (
    <UpgradeContext.Provider value={{ open: openUpgrade }}>
      <ShareProvider>
        <div className="app-shell-bg">
          <div className="app-shell pb-[110px] min-h-screen relative">
            <NotificationsBanner />
            <Outlet />
            <BottomNav />
          </div>
        </div>
        <UpgradeModal open={modal.open} onClose={closeUpgrade} feature={modal.feature} />
      </ShareProvider>
    </UpgradeContext.Provider>
  );
}

export const useUpgradeModal = () => React.useContext(UpgradeContext);
